import { Formik, useFormikContext } from 'formik'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { AuthSessionContext } from '@context/AuthContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Heading6, Title1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { SimpleList } from '@ui/components/SimpleList'
import { CalendarIcon } from '@ui/icons/Calendar.icon'

import { SupportedDateFormatType, formatDate } from '@utils/date.utils'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { TestAnswersDto } from '@model/ClientTestAnswerDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'
import { FreeTextAnswer, TestSectionQuestionDto } from '@model/TestSectionsDto'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'
import { RawSelectFormField } from '@global/formField/FormSelectField'

import { Box, BoxProps } from '@shared/components/Box'
import { validateTestForm } from '@shared/templates/Test/TestFields/TestFields'

import styles from './W8BeneficialForm.module.scss'

const FormCheckBoxField = createFormCheckBoxField()
const FormTextField = createFormField()

const EXCLUDED_FROM_GENERIC_VALIDATION = ['mailing_same_as_residence', 'certify_no_us_tax_benefits']

export interface W8BeneficialFormValues {
  freeAnswers: FreeTextAnswer[]
  selectedAnswers: string[]
  [key: string]: any
}

interface W8BeneficialFormProps {
  regulators: RegulatorOptionDto[]
  taxFormQuestions: ClientTestDto
  taxFormAnswers: TestAnswersDto | undefined
  account: AccountDetailedDto
  onFormSubmit: (values: W8BeneficialFormValues) => void
  isFormEnabled: boolean
}

const W8Field: React.FC<{
  widgetName: string
  questions: TestSectionQuestionDto[]
  disabled?: boolean
  required?: boolean
  regulators?: RegulatorOptionDto[]
  customComponent?: React.ReactNode
  gridColumn?: BoxProps['gridColumn']
}> = ({ widgetName, questions, disabled, required, regulators, customComponent, gridColumn }) => {
  const { values, setValues } = useFormikContext<W8BeneficialFormValues>()
  const questionData = questions.find((q) => q.widget.name === widgetName)

  if (!questionData) return null
  const { question, widget, answers } = questionData
  const commonProps = {
    name: widget.name,
    label: question.name,
    placeholder: question.name,
    disabled,
    required,
  }

  const updateSelectedAnswer = (
    currentSelectedAnswers: string[],
    answers: { id: string }[],
    nextAnswerId: string
  ) => {
    const answerIdsForThisQuestion = new Set(answers.map((a) => a.id))

    return [
      ...currentSelectedAnswers.filter((id) => !answerIdsForThisQuestion.has(id)),
      nextAnswerId,
    ]
  }

  if (widgetName === 'tax_country_id' || widgetName === 'mailing_country') {
    const countryOptions = regulators?.map((regulator) => ({
      id: regulator.country.id,
      name: regulator.country.name,
    }))

    return (
      <Box gridColumn={gridColumn} minWidth={0}>
        <SensitiveData>
          <RawSelectFormField
            {...commonProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value
              setValues({
                ...values,
                [widget.name]: val,
                freeAnswers: updateFreeAnswers(values.freeAnswers, question.id, val),
              })
            }}
            options={countryOptions}
            hasCheckMark={false}
            dropdownListVariant='compact'
          />
        </SensitiveData>
      </Box>
    )
  }
  if (widget.type === 'select' || widgetName === 'country_of_citizenship') {
    return (
      <Box gridColumn={gridColumn} minWidth={0}>
        <SensitiveData>
          <RawSelectFormField
            {...commonProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value

              setValues({
                ...values,
                [widget.name]: val,
                selectedAnswers: updateSelectedAnswer(values.selectedAnswers, answers, val),
              })
            }}
            options={
              widgetName === 'country_of_citizenship'
                ? (values.nationalities || []).map((n: any) => ({ id: n.id, name: n.name }))
                : answers.map((a) => ({
                    id: a.id,
                    name: a.name,
                  }))
            }
            hasCheckMark={false}
            dropdownListVariant='compact'
          />
        </SensitiveData>
      </Box>
    )
  }

  if (widgetName === 'certify_country_id') {
    const getCountryName = (id: string) =>
      regulators?.find((r) => r.country.id === id || r.country.name === id)?.country.name

    return (
      <Box gridColumn={gridColumn} minWidth={0}>
        <SensitiveData>
          <FormTextField
            disabled
            value={getCountryName(values['certify_country_id'])}
            type='text'
          />
        </SensitiveData>
      </Box>
    )
  }

  return (
    <Box gridColumn={gridColumn} minWidth={0}>
      <SensitiveData>
        {customComponent || (
          <FormTextField
            {...commonProps}
            type='text'
            onChange={(e: any) => {
              const val = e.target.value
              setValues({
                ...values,
                [widget.name]: val,
                freeAnswers: updateFreeAnswers(values.freeAnswers, question.id, val),
              })
            }}
          />
        )}
      </SensitiveData>
    </Box>
  )
}

const updateFreeAnswers = (
  currentAnswers: FreeTextAnswer[],
  questionId: string,
  answer: string
) => {
  const filtered = currentAnswers.filter((fa) => fa.testQuestionId !== questionId)
  return [...filtered, { testQuestionId: questionId, answer }]
}

const W8BeneficialFormContent: React.FC<W8BeneficialFormProps> = ({
  regulators,
  taxFormQuestions,
  account,
  isFormEnabled,
}) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const { values, setValues, isValid, isSubmitting, errors } =
    useFormikContext<W8BeneficialFormValues>()
  const dateFormat = auth?.dateFormatType?.name
  const allQuestions = taxFormQuestions.sections.flatMap((s) => s.questions)

  const qOwner = (name: string) =>
    allQuestions.find((q) => q.widget.name === name)?.question || { id: '', name: '' }
  const wOwner = (name: string) => allQuestions.find((q) => q.widget.name === name)?.widget

  const mailingSameAsResidenceWidget = wOwner('mailing_same_as_residence')
  const mailingSameAsResidenceQuestion = qOwner('mailing_same_as_residence')

  return (
    <Box
      flex
      direction='column'
      gap='x48'
      my='x16'
      p='x24'
      bg='surface-surface-1'
      radius='x8'
      className={styles.card}
    >
      <Box flex direction='column' gap='x24'>
        <Heading6 medium>{t('W8.Identification of Beneficial Owner')}</Heading6>

        <Box
          grid
          gridTemplateColumns={{ mobile: 'minmax(0, 1fr)', desktop: 'repeat(2, minmax(0, 1fr))' }}
          gap='x16'
        >
          <W8Field widgetName='name_of_individual' questions={allQuestions} disabled required />

          <W8Field
            widgetName='country_of_citizenship'
            gridColumn='1'
            questions={allQuestions}
            disabled={!isFormEnabled}
            required
          />

          <SensitiveData>
            <FormTextField
              name='birthdate'
              type='text'
              label={qOwner('birthdate').name}
              value={formatDate(values.birthdate, {
                formatType: (account.dateFormatType?.name ||
                  'dd.MM.yyyy') as SupportedDateFormatType,
                noTimezone: true,
              })}
              required
              disabled
              showLabel
              rightIcon={CalendarIcon}
            />
          </SensitiveData>
        </Box>
      </Box>
      <Box flex direction='column' gap='x24'>
        <Title1 isParagraph medium>
          {t('W8.Permanent Residence Address')}
        </Title1>
        <Box
          grid
          gridTemplateColumns={{ mobile: 'minmax(0, 1fr)', desktop: 'repeat(2, minmax(0, 1fr))' }}
          gap='x16'
        >
          <W8Field
            widgetName='residence_country'
            gridColumn='1 / -1'
            questions={allQuestions}
            disabled
            required
            regulators={regulators}
          />

          <W8Field widgetName='residence_city' questions={allQuestions} disabled required />
          <W8Field widgetName='residence_county' questions={allQuestions} disabled required />

          <W8Field widgetName='residence_street' questions={allQuestions} disabled required />
          <W8Field widgetName='residence_postal_code' questions={allQuestions} disabled required />

          <W8Field widgetName='residence_street_house_no' questions={allQuestions} disabled />
        </Box>
      </Box>
      <Box flex direction='column' gap='x24'>
        <Title1 isParagraph medium>
          {t('W8.Mailing Address')}
        </Title1>
        <Box flex direction='column' gap='x16'>
          <FormCheckBoxField
            name={mailingSameAsResidenceWidget?.name}
            disabled={!isFormEnabled}
            onChange={(checked) => {
              const isChecked = checked
              const qId = mailingSameAsResidenceQuestion.id

              const newFreeAnswers = values.freeAnswers.filter(
                (x) => x.testQuestionId !== qId && !x.testQuestionId.startsWith('mailing_')
              )

              newFreeAnswers.push({
                testQuestionId: qId,
                answer: isChecked ? '1' : '0',
              })

              setValues({
                ...values,
                [mailingSameAsResidenceWidget!.name]: isChecked,
                freeAnswers: newFreeAnswers,
              })
            }}
          >
            <Body1 isParagraph>{t('W8.Same as Permanent Residence Address')}</Body1>
          </FormCheckBoxField>

          {!values.mailing_same_as_residence && (
            <Box
              grid
              gridTemplateColumns={{
                mobile: 'minmax(0, 1fr)',
                desktop: 'repeat(2, minmax(0, 1fr))',
              }}
              gap='x16'
            >
              <W8Field
                widgetName='mailing_country'
                gridColumn='1 / -1'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
                regulators={regulators}
              />

              <W8Field
                widgetName='mailing_city'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
              />
              <W8Field
                widgetName='mailing_county'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
                required
              />

              <W8Field
                widgetName='mailing_street'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
              />
              <W8Field
                widgetName='mailing_postal_code'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
              />

              <W8Field
                widgetName='mailing_street_house_no'
                questions={allQuestions}
                disabled={values.mailing_same_as_residence || !isFormEnabled}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box flex direction='column' gap='x24'>
        <Title1 isParagraph medium>
          {t('W8.Tax Info')}
        </Title1>
        <Box
          grid
          gridTemplateColumns={{ mobile: 'minmax(0, 1fr)', desktop: 'repeat(2, minmax(0, 1fr))' }}
          gap='x16'
        >
          <W8Field widgetName='us_tin' questions={allQuestions} disabled={!isFormEnabled} />
          <W8Field widgetName='us_tin_type' questions={allQuestions} disabled={!isFormEnabled} />

          <W8Field
            widgetName='tax_country_id'
            questions={allQuestions}
            disabled={!isFormEnabled}
            regulators={regulators}
          />
          <W8Field
            widgetName='tax_country_tin'
            questions={allQuestions}
            disabled={!isFormEnabled}
          />

          <W8Field
            widgetName='birth_country_id'
            questions={allQuestions}
            disabled={!isFormEnabled}
            regulators={regulators}
          />
          <W8Field
            widgetName='citytown_of_birth'
            questions={allQuestions}
            disabled={!isFormEnabled}
          />
        </Box>
      </Box>
      <Box flex direction='column' gap='x24'>
        <Heading6 medium>{t('W8.Claim of Treaty Benefits')}</Heading6>
        <Box flex direction='column' gap='x12'>
          <Body1 isParagraph>{t(`W8.I certify that the beneficial`)}</Body1>

          <Box flex direction='column' gap='x8'>
            {taxFormQuestions.sections
              .find((s) => s.code === 'claim_of_treaty_benefits')
              ?.questions.map((q) => {
                if (q.widget.type === 'short_text') {
                  return (
                    <W8Field
                      key={q.question.id}
                      widgetName={q.widget.name}
                      questions={allQuestions}
                      disabled
                      regulators={regulators}
                      required
                    />
                  )
                }
                if (q.widget.type === 'checkbox') {
                  return (
                    <FormCheckBoxField
                      key={q.question.id}
                      disabled={!isFormEnabled}
                      name={q.widget.name}
                      onChange={(checked) => {
                        setValues({
                          ...values,
                          [q.widget.name]: checked,
                          freeAnswers: updateFreeAnswers(
                            values.freeAnswers,
                            q.question.id,
                            checked ? '1' : '0'
                          ),
                        })
                      }}
                    >
                      <Body1 isParagraph>
                        {t('W8.I do not qualify for U.S. tax treaty benefits')}
                      </Body1>
                    </FormCheckBoxField>
                  )
                }
                return null
              })}
          </Box>
        </Box>
      </Box>
      <Box flex direction='column' gap='x24'>
        <Heading6 medium>{t('W8.Certification')}</Heading6>
        <Box flex direction='column' gap='x16' p='x24' border='outline-high-em' radius='x8'>
          <Body1 isParagraph>{t('W8.Under penalties of perjury')}</Body1>
          <SimpleList>
            <li>{t('W8.I am the individual')}</li>
            <li>{t('W8.The beneficial owner is not a US person;')}</li>
            <li>
              {t('W8.This form relates to')}
              <Body1 isParagraph>{t('W8.income not effectively connected')}</Body1>
              <Body1 isParagraph>{t('W8.income effectively connected')}</Body1>
              <Body1 isParagraph>{t("W8.the partner's share")}</Body1>
              <Body1 isParagraph>{t("W8.the partner's amount realized")};</Body1>
            </li>

            <li>{t('W8.The beneficial owner listed on this form is a')}</li>
            <li>{t('W8.For broker transactions')}</li>
          </SimpleList>
          <Body1 isParagraph>{t('W8.Furthermore, I authorize')}</Body1>
        </Box>

        <FormCheckBoxField
          disabled={!isFormEnabled}
          name='certify_capacity_to_sign'
          error={errors.certify_capacity_to_sign?.toString()}
          label={qOwner('certify_capacity_to_sign').name}
          onChange={(checked) => {
            setValues({
              ...values,
              certify_capacity_to_sign: checked,
              freeAnswers: updateFreeAnswers(
                values.freeAnswers,
                qOwner('certify_capacity_to_sign').id,
                checked ? '1' : '0'
              ),
            })
          }}
        />
        <Box
          grid
          gridTemplateColumns={{ mobile: 'minmax(0, 1fr)', desktop: 'repeat(3, minmax(0, 1fr))' }}
          gap='x16'
        >
          <W8Field widgetName='name_of_signer' questions={allQuestions} disabled />

          <SensitiveData>
            <FormTextField
              name='certification_date'
              type='text'
              label={qOwner('certification_date').name}
              value={formatDate(values.certification_date, { formatType: dateFormat })}
              disabled
              showLabel
              rightIcon={CalendarIcon}
            />
          </SensitiveData>

          <W8Field
            widgetName='certification_signature'
            questions={allQuestions}
            disabled={!isFormEnabled}
          />
        </Box>
      </Box>
      <Box flex justify='center'>
        <Button.Primary size='lg' type='submit' disabled={!isValid || isSubmitting}>
          {t('Submit')}
        </Button.Primary>
      </Box>
    </Box>
  )
}

export const W8BeneficialForm: React.FC<W8BeneficialFormProps> = (props) => {
  const { account, taxFormQuestions, taxFormAnswers, isFormEnabled, onFormSubmit } = props
  const { t } = useTranslation()

  const initialValues = useMemo(() => {
    const allowRetake = taxFormAnswers?.allowRetake

    const getAnswer = (widgetName: string, fallback: string | boolean) => {
      if (!isFormEnabled || allowRetake) {
        const found = taxFormAnswers?.sections
          .flatMap((s) => s.answers)
          .find((a) => a.question.widgetName === widgetName)
        if (found) return found.answer.id || found.answer.name
      }
      return fallback
    }

    const nationality = account.nationalities.find((n) => n.isPrimary)
    const address = account.addresses.find((c) => c.isPrimary)
    const name = [account.firstname, account.middlename, account.lastname].filter(Boolean).join(' ')

    const defaults: any = {
      name_of_individual: name,
      country_of_citizenship: nationality?.id || '',
      residence_country: address?.country.name || '',
      residence_city: address?.city || '',
      residence_county: address?.state || '',
      residence_street: address?.street || '',
      residence_postal_code: address?.postalCode || '',
      mailing_same_as_residence: true,
      mailing_country: address?.country.id || '',
      mailing_city: address?.city || '',
      mailing_county: address?.state || '',
      mailing_street: address?.street || '',
      mailing_postal_code: address?.postalCode || '',
      mailing_street_house_no: address?.streetHouseNo || '',
      us_tin: '',
      us_tin_type: '',
      tax_country_id: nationality?.id || '',
      tax_country_tin: '',
      birth_country_id: nationality?.id || '',
      citytown_of_birth: '',
      birthdate: account.birthday,
      certify_country_id: address?.country.id || '',
      certify_no_us_tax_benefits: false,
      name_of_signer: name,
      certification_date: new Date().toISOString(),
      certification_signature: '',
      certify_capacity_to_sign: false,
      residence_street_house_no: address?.streetHouseNo || '',
    }

    const values: any = {
      freeAnswers: [],
      selectedAnswers: [],
      nationalities: account.nationalities,
    }

    const allQuestions = taxFormQuestions.sections.flatMap((s) => s.questions)

    Object.keys(defaults).forEach((key) => {
      const value = getAnswer(key, defaults[key])
      values[key] = value

      const question = allQuestions.find((q) => q.widget.name === key)
      if (question) {
        let answerValue = value
        if (question.widget.type === 'checkbox' && typeof value === 'boolean') {
          answerValue = value ? '1' : '0'
        }

        if (answerValue !== undefined && answerValue !== null && answerValue !== '') {
          if (question.widget.type === 'select' || question.widget.type === 'radio') {
            values.selectedAnswers.push(String(answerValue))
          } else {
            values.freeAnswers.push({
              testQuestionId: question.question.id,
              answer: String(answerValue),
            })
          }
        }
      }
    })

    if (typeof values.mailing_same_as_residence === 'string') {
      values.mailing_same_as_residence = values.mailing_same_as_residence !== '0'
    }
    if (typeof values.certify_capacity_to_sign === 'string') {
      values.certify_capacity_to_sign = values.certify_capacity_to_sign !== '0'
    }
    if (typeof values.certify_no_us_tax_benefits === 'string') {
      values.certify_no_us_tax_benefits = values.certify_no_us_tax_benefits !== '0'
    }

    if (values.mailing_same_as_residence) {
      values.mailing_country = values.residence_country
      values.mailing_county = values.residence_county
      values.mailing_city = values.residence_city
      values.mailing_street = values.residence_street
      values.mailing_postal_code = values.residence_postal_code
      values.mailing_street_house_no = values.residence_street_house_no

      const mailingFields = [
        { widget: 'mailing_country', value: values.residence_country },
        { widget: 'mailing_county', value: values.residence_county },
        { widget: 'mailing_city', value: values.residence_city },
        { widget: 'mailing_street', value: values.residence_street },
        { widget: 'mailing_postal_code', value: values.residence_postal_code },
        { widget: 'mailing_street_house_no', value: values.residence_street_house_no },
      ]

      mailingFields.forEach((field) => {
        const question = allQuestions.find((q) => q.widget.name === field.widget)
        if (question && field.value) {
          values.freeAnswers = values.freeAnswers.filter(
            (x: any) => x.testQuestionId !== question.question.id
          )
          values.freeAnswers.push({
            testQuestionId: question.question.id,
            answer: String(field.value),
          })
        }
      })
    }

    return values as W8BeneficialFormValues
  }, [account, taxFormQuestions, taxFormAnswers, isFormEnabled])

  const sectionsForValidation = useMemo(
    () =>
      taxFormQuestions.sections.map((section) => ({
        ...section,
        questions: section.questions.filter(
          (q) => !EXCLUDED_FROM_GENERIC_VALIDATION.includes(q.widget.name)
        ),
      })),
    [taxFormQuestions.sections]
  )

  const validate = (values: W8BeneficialFormValues) => {
    const errors: any = validateTestForm(sectionsForValidation, values as any, t)

    if (!values.certification_signature) {
      errors.certification_signature = t('Validation.Required')
    } else if (values.certification_signature !== values.name_of_individual) {
      errors.certification_signature = t('Validation.Signature must match with the name of signer')
    }

    if (!values.mailing_same_as_residence) {
      if (!values.mailing_country) errors.mailing_country = t('Validation.Required')
      if (!values.mailing_county) errors.mailing_county = t('Validation.Required')
      if (!values.mailing_city) errors.mailing_city = t('Validation.Required')
      if (!values.mailing_street) errors.mailing_street = t('Validation.Required')
      if (!values.mailing_postal_code) errors.mailing_postal_code = t('Validation.Required')
    }

    if (!values.certify_capacity_to_sign) {
      errors.certify_capacity_to_sign = t('Validation.Required')
    }

    return errors
  }

  const setAnswers = (values: W8BeneficialFormValues): W8BeneficialFormValues => {
    const allQuestions = taxFormQuestions.sections.flatMap((s) => s.questions)
    const freeAnswers = [...values.freeAnswers]
    const selectedAnswers: string[] = []

    allQuestions.forEach((question) => {
      const fieldValue = values[question.widget.name]

      if (question.widget.type === 'select' || question.widget.type === 'radio') {
        if (fieldValue) {
          selectedAnswers.push(String(fieldValue))
        }

        return
      }

      if (!freeAnswers.some((x) => x.testQuestionId === question.question.id)) {
        let answer = fieldValue

        if (question.widget.type === 'checkbox') {
          answer = answer ? '1' : '0'
        }

        if (answer !== undefined && answer !== null && answer !== '') {
          freeAnswers.push({
            testQuestionId: question.question.id,
            answer: String(answer),
          })
        }
      }
    })

    return {
      ...values,
      freeAnswers,
      selectedAnswers,
    }
  }
  const handleSubmit = (values: W8BeneficialFormValues, { setSubmitting }: any) => {
    const payload = setAnswers(values)
    onFormSubmit(payload)
    setSubmitting(false)
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      enableReinitialize
      isInitialValid={false}
    >
      <Form className={styles.form}>
        <W8BeneficialFormContent {...props} />
      </Form>
    </Formik>
  )
}
